import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { Grid, IconButton, MenuItem, Theme, Tooltip } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import makeStyles from '@mui/styles/makeStyles';
import { useMemo } from 'react';
import { LabelledOutline } from '../../../common/final-form-material-ui/LabelledOutline';
import { FormikSelect } from '../../../common/formik-material-ui/FormikSelect';
import InfoPanel from '../InfoPanel/InfoPanel';
import NumberTabAutoComplete from './NumberTabAutoComplete';

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    height: 'max-content',
    alignSelf: 'center',
  },
  field: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  fieldSelectCol: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function NumberTabActions({
  formik,
  businessNumbersLoadable,
  businessNumbersCached,
  id,
  groupDetails,
  agentDetails,
  queueDetails,
  conferenceDetails,
  ivrDetails,
}) {
  const classes = useStyles();
  const numberForOptions = [
    'None',
    'Agent',
    'Conference',
    'Group',
    'IVR',
    'Local Presence',
    'Connection Boost Pool',
    'Queue',
    'Unallocated',
    'Number',
  ];

  const localPresence = useMemo(
    () =>
      businessNumbersLoadable.state === 'hasValue'
        ? [...(businessNumbersLoadable.contents?.response?.data?.result ?? [])]
            ?.filter(
              (el) =>
                el.active === 1 && el.type.toLowerCase() === 'local presence'
            )
            .map((el) => el.DID)
        : [],
    [businessNumbersLoadable]
  );

  const unallocated = useMemo(
    () =>
      businessNumbersLoadable.state === 'hasValue'
        ? [...(businessNumbersLoadable.contents?.response?.data?.result ?? [])]
            ?.filter(
              (el) => el.active === 1 && el.type.toLowerCase() === 'unallocated'
            )
            .map((el) => el.DID)
        : [],
    [businessNumbersLoadable]
  );

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: 500,
  });

  return (
    <div className="container">
      <InfoPanel>
        <div>
          Setup Inbound Call Routing by first selecting a number or group of
          numbers on this tab. Select a Number Type in the first dropdown, then
          select a corresponding number or group of numbers in the second
          dropdown. Click the + icon to add additional numbers, click the trash
          icon to remove. All number types selected will follow the same
          conditions setup in the next tab.
        </div>
      </InfoPanel>
      {formik.values.numberActions.map((number, index) => {
        return (
          <div className={classes.flexRow} key={index}>
            <div
              className={classes.fieldSelectCol}
              style={{ width: '90%', paddingRight: '12.5px' }}
            >
              {businessNumbersLoadable?.state === 'loading' ? (
                <Skeleton variant="rectangular" />
              ) : (
                <LabelledOutline key={index} label={`Number ${index + 1}`}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                      <FormikSelect
                        id="standard-basic"
                        label="Number For"
                        name={`numberActions.${index}.numberFor`}
                        formik={formik}
                        size="small"
                        variant="outlined"
                        fullWidth
                        data-pup="add-routing-rule-number-for"
                      >
                        {numberForOptions.map((option, index) => (
                          <MenuItem
                            disabled={
                              formik.values.numberActions.filter(
                                (num) => num.numberFor === option
                              ).length !== 0
                            }
                            key={index}
                            value={option}
                            data-pup={`routing-rule-number-for-${option}`}
                          >
                            {option === 'Local Presence'
                              ? 'Connection Boost'
                              : option}
                          </MenuItem>
                        ))}
                      </FormikSelect>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                      {number?.numberFor === 'Local Presence' &&
                        !!businessNumbersCached.current?.allNumbers?.length && (
                          <NumberTabAutoComplete
                            dataPup={`routing-rule-select-local-presence`}
                            items={localPresence ?? []}
                            index={index}
                            formik={formik}
                            field={`numberActions.${index}.localPresence`}
                            getOptionLabel={(option) =>
                              option === 'selectAll' ? 'Select All' : option
                            }
                            label={'Connection Boost'}
                            selected={
                              formik.values.numberActions[index].localPresence
                            }
                            filterOptions={filterOptions}
                          />
                        )}
                      {number?.numberFor === 'Agent' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-agent`}
                          items={agentDetails?.ids ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.agent`}
                          getOptionLabel={(option) => {
                            return option === 'selectAll'
                              ? 'Select All'
                              : `${agentDetails?.lookup?.[option]?.fname} ${agentDetails?.lookup?.[option]?.lname}`;
                          }}
                          label={'Agent'}
                          selected={formik.values.numberActions[index].agent}
                        />
                      )}
                      {number?.numberFor === 'Conference' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-conference`}
                          items={conferenceDetails?.ids ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.conference`}
                          getOptionLabel={(option) =>
                            option === 'selectAll'
                              ? 'Select All'
                              : `${
                                  conferenceDetails?.lookup?.[option]?.name ??
                                  ''
                                }`
                          }
                          label={'Conference'}
                          selected={
                            formik.values.numberActions[index].conference
                          }
                        />
                      )}
                      {number?.numberFor === 'Group' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-group`}
                          items={groupDetails?.ids ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.group`}
                          getOptionLabel={(option) =>
                            option === 'selectAll'
                              ? 'Select All'
                              : `${groupDetails?.lookup?.[option]?.groupname}`
                          }
                          label={'Group'}
                          selected={formik.values.numberActions[index].group}
                        />
                      )}
                      {number?.numberFor === 'IVR' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-ivr`}
                          items={ivrDetails?.ids ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.ivr`}
                          getOptionLabel={(option) =>
                            option === 'selectAll'
                              ? 'Select All'
                              : `${ivrDetails?.lookup?.[option]?.IVRname}`
                          }
                          label={'IVR'}
                          selected={formik.values.numberActions[index].ivr}
                        />
                      )}
                      {number?.numberFor === 'Queue' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-queue`}
                          items={queueDetails?.ids ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.queue`}
                          getOptionLabel={(option) =>
                            option === 'selectAll'
                              ? 'Select All'
                              : `${queueDetails?.lookup?.[option]?.queuename}`
                          }
                          label={'Queue'}
                          selected={formik.values.numberActions[index].queue}
                        />
                      )}
                      {number?.numberFor === 'Unallocated' && (
                        <NumberTabAutoComplete
                          dataPup={`routing-rule-select-unallocated`}
                          items={unallocated ?? []}
                          index={index}
                          formik={formik}
                          field={`numberActions.${index}.unallocated`}
                          getOptionLabel={(option) =>
                            option === 'selectAll' ? 'Select All' : option
                          }
                          label={'Unallocated'}
                          selected={
                            formik.values.numberActions[index].unallocated
                          }
                          filterOptions={filterOptions}
                        />
                      )}
                      {number?.numberFor === 'Number' &&
                        !!businessNumbersCached.current?.allNumbers?.length && (
                          <NumberTabAutoComplete
                            dataPup={`routing-rule-select-number`}
                            items={
                              businessNumbersCached?.current?.allNumbers ?? []
                            }
                            index={index}
                            formik={formik}
                            field={`numberActions.${index}.number`}
                            getOptionLabel={(option) =>
                              option === 'selectAll'
                                ? 'Select All'
                                : `${option} - ${
                                    businessNumbersCached?.current
                                      ?.lookupNumbers?.[option]?.type ===
                                    'Local Presence'
                                      ? 'Connection Boost'
                                      : businessNumbersCached?.current
                                          ?.lookupNumbers?.[option]?.type
                                  }${
                                    businessNumbersCached?.current
                                      ?.lookupNumbers?.[option]
                                      ?.destinationName !== 'Local Presence' &&
                                    businessNumbersCached?.current
                                      ?.lookupNumbers?.[option]
                                      ?.destinationName !== 'unallocated'
                                      ? ': ' +
                                        businessNumbersCached?.current
                                          ?.lookupNumbers?.[option]
                                          ?.destinationName
                                      : ''
                                  }`
                            }
                            label={'Numbers'}
                            selected={formik.values.numberActions[index].number}
                            filterOptions={filterOptions}
                          />
                        )}
                    </Grid>
                  </Grid>
                </LabelledOutline>
              )}
            </div>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                flexWrap: 'nowrap',
                paddingLeft: '8px',
              }}
            >
              <Tooltip title="Add">
                <>
                  <IconButton
                    className={classes.icon}
                    aria-label="add"
                    color="primary"
                    onClick={() => {
                      formik.setFieldValue('numberActions', [
                        ...formik.values.numberActions.slice(0, index + 1),
                        {
                          numberFor: 'None',
                          selectAll: false,
                          agent: [],
                          conference: [],
                          group: [],
                          ivr: [],
                          queue: [],
                          unallocated: [],
                          localPresence: [],
                          number: [],
                        } as any,
                        ...formik.values.numberActions.slice(index + 1),
                      ]);
                    }}
                    disabled={formik.values.numberActions.length >= 8}
                    size="large"
                  >
                    <PlaylistAddIcon />
                  </IconButton>
                </>
              </Tooltip>
              <Tooltip key={index} title="Remove">
                <>
                  <IconButton
                    className={classes.deleteIconButton}
                    aria-label="cancel"
                    onClick={() => {
                      const numberActions =
                        id !== null
                          ? [
                              ...formik.values.numberActions.slice(0, index),
                              ...formik.values.numberActions.slice(index + 1),
                            ]
                          : [
                              ...formik.values.numberActions.slice(0, index),
                              ...formik.values.numberActions.slice(index + 1),
                            ];
                      formik.setFieldValue('numberActions', numberActions);
                    }}
                    disabled={formik.values.numberActions.length <= 1}
                    size="large"
                  >
                    <DeleteIcon />
                  </IconButton>
                </>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
}
